import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { bloodTestsAPI } from '../../services/api'

/**
 * VH-221: "n markers excluded" next to the score, with the list and the
 * reason for each. Also carries the score's notices (few markers, context
 * check could not run).
 */
export default function ExcludedMarkersNotice({ bloodTestId, excludedCount, contextCheckStatus }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['bloodTestScore', bloodTestId],
    queryFn: async () => (await bloodTestsAPI.getScore(bloodTestId)).data,
    enabled: Boolean(bloodTestId && (expanded || excludedCount > 0)),
  })

  const count = data?.excluded_count ?? excludedCount ?? 0
  const notices = data?.notices || []
  const checkFailed = (data?.context_check_status ?? contextCheckStatus) === 'failed'

  if (!count && !checkFailed && notices.length === 0) return null

  return (
    <div className="mt-3 text-sm text-left">
      {checkFailed && (
        <p className="text-amber-700">{t('bloodTests.context.checkFailed')}</p>
      )}
      {notices.includes('score_rests_on_few_markers') && (
        <p className="text-amber-700">{t('bloodTests.context.fewMarkers')}</p>
      )}
      {count > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-primary-600 hover:text-primary-700 underline"
          aria-expanded={expanded}
        >
          {t('bloodTests.context.excludedCount', { count })}
        </button>
      )}
      {expanded && (
        <div className="mt-2">
          {isLoading && <p className="text-gray-500">{t('bloodTests.context.loading')}</p>}
          {data?.excluded?.length > 0 && (
            <ul className="space-y-1">
              {data.excluded.map((item, idx) => (
                <li key={`${item.result_id ?? idx}-${item.reason}`} className="text-gray-700">
                  <span className="font-medium">{item.name}</span>
                  {' — '}
                  {t(`bloodTests.context.reasons.${item.reason}`, {
                    defaultValue: item.reason,
                    marker: item.counted_as || '',
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
